import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { capabilityGate } from '@hopdrive/feature-portal-products';
import { useProductEntry } from '@features/products/useProductEntry';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Typography,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { MdArrowBack, MdDirectionsCar, MdPlace, MdCheck, MdWarning, MdLogin, MdLogout } from 'react-icons/md';
import DefaultFallback from '@components/Fallbacks/DefaultFallback';
import Toolbar from '@components/Utils/Toolbar';
import { useGetPartsRunDetailQuery } from '../queries/getPartsRunDetail.query.generated';
import ReassignDriverDialog from '../components/ReassignDriverDialog';
import UnassignDriverDialog from '../components/UnassignDriverDialog';
import { buildPodBlocks, hasPod } from '../utils/podBlocks';
import CourierRunOverview from '../components/CourierRunOverview';
import { useThirdPartyDelivery } from '../thirdParty/useThirdPartyDelivery';
import RunDetailOffer from '@components/RunDetailOffer';
import { useGetOfferedDriverQuery } from '@features/mobileService/queries/getOfferedDriver.query.generated';
import RunStatusChip from '@components/Badges/RunStatusChip';
import { MOBILITY_RUN_TILE_STATUSES } from '@hopdrive/feature-order-index';

/** Completed or canceled (both spellings): nothing further happens to the run. */
const SETTLED_RUN_STATUSES = new Set<string>([...MOBILITY_RUN_TILE_STATUSES.completed, ...MOBILITY_RUN_TILE_STATUSES.cancelled]);
const isSettledRunStatus = (status: string): boolean => SETTLED_RUN_STATUSES.has(status);

const ORDER_STATUS_CHIPS: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'info' | 'error' }> = {
  pending: { label: 'Pending', color: 'default' },
  assigned: { label: 'Assigned', color: 'info' },
  loaded: { label: 'Loaded', color: 'info' },
  in_transit: { label: 'In Transit', color: 'warning' },
  delivered: { label: 'Delivered', color: 'success' },
  partial: { label: 'Partial', color: 'warning' },
  rejected: { label: 'Rejected', color: 'error' },
};

/** Label + color for the outcome note line, keyed by delivery_status. */
const NOTE_META: Record<string, { label: string; color: string }> = {
  rejected: { label: 'Reason', color: 'error.main' },
  partial: { label: 'Delivered', color: 'warning.main' },
};

/** Poll cadence while a dispatched run's offer row is still landing. */
const DISPATCH_POLL_INTERVAL_MS = 3_000;
/** Stop waiting for a dispatch this page sent after this long (a dropped hop never lands). */
const DISPATCH_POLL_MAX_MS = 60_000;

const RunDetailPage: React.FC = () => {
  const { id: rawId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const runId = parseInt(rawId ?? '', 10);
  // Managing the run's driver is gated on the Parts Delivery product's manage capability.
  const canManage = capabilityGate(useProductEntry('parts-delivery'), 'manageExisting').kind === 'render';

  const { data, loading, error, refetch, startPolling, stopPolling } = useGetPartsRunDetailQuery({
    variables: { id: runId },
    skip: Number.isNaN(runId),
    fetchPolicy: 'cache-and-network',
  });

  const run = data?.mobility_runs_by_pk;

  // Courier-fulfilled runs render a different overview and poll the live
  // third-party delivery state instead of the driver/offer machinery.
  const isCourierRun = (run as any)?.fulfillment_type === 'external_courier';

  // The active bundle for this run, and its live offers.
  const activeBundle = run?.scenario?.bundletoscenarios?.[0]?.bundle ?? null;
  const activeOffers = activeBundle?.offers ?? [];
  const acceptedOffer = activeOffers.find(o => o.status === 'accepted' || o.status === 'assigned');

  // Targeted-dispatch fallback (same as the mobile-service run page): when neither
  // run.driver nor an offer row names a driver yet, bundle.config.driverIdOverride is the
  // synchronous record of who the dispatch went to — the offers row lands seconds later via
  // the async bundle→batchjob chain (or never, on a dropped hop). Resolve that id so the
  // panel reads "Dispatching…" during the gap instead of "No driver assigned" + Assign.
  const offeredDriver = activeOffers[0]?.driver ?? null;
  const pendingTargetId =
    !isCourierRun && !run?.driver && !offeredDriver
      ? Number((activeBundle?.config as { driverIdOverride?: unknown } | null)?.driverIdOverride) || null
      : null;
  const { data: pendingDriverData } = useGetOfferedDriverQuery({
    variables: { driverId: pendingTargetId ?? 0 },
    skip: !pendingTargetId,
  });
  const pendingDriver = pendingTargetId ? pendingDriverData?.drivers_by_pk ?? null : null;

  // Set by a successful (re-)dispatch from the dialog: the page fetches the run once, so
  // without polling the async offer row never shows up until a manual refresh. Cleared as
  // soon as a driver or offer appears, or when the bounded wait below gives up.
  const [dispatchInFlight, setDispatchInFlight] = React.useState(false);
  // Bumped by each dispatch from this page, so a re-dispatch restarts the wait.
  const [awaitGeneration, setAwaitGeneration] = React.useState(0);
  const [awaitTimedOut, setAwaitTimedOut] = React.useState(false);
  const hasLandedDriver = !!(run?.driver || activeOffers.length);
  React.useEffect(() => {
    if (hasLandedDriver) setDispatchInFlight(false);
  }, [hasLandedDriver]);

  // An own-driver dispatch is pending: a targeted dispatch whose offer hasn't landed yet
  // (bundle.config.driverIdOverride, already there on page load), or one this page just
  // sent. BOUNDED either way: a dropped hop never lands, and a run stuck in that state
  // must not keep the page polling every few seconds for as long as it stays open.
  const shouldAwaitOffer = !isCourierRun && !hasLandedDriver && (!!pendingTargetId || dispatchInFlight);
  React.useEffect(() => {
    if (!shouldAwaitOffer) {
      setAwaitTimedOut(false);
      return undefined;
    }
    setAwaitTimedOut(false);
    const timeout = setTimeout(() => {
      setAwaitTimedOut(true);
      setDispatchInFlight(false);
    }, DISPATCH_POLL_MAX_MS);
    return () => clearTimeout(timeout);
  }, [shouldAwaitOffer, awaitGeneration]);
  const awaitingOffer = shouldAwaitOffer && !awaitTimedOut;

  // Courier runs: keep the stop/run status fresh while the courier progresses
  // (useThirdPartyDelivery mirrors the courier state onto those rows). Own-
  // driver runs are fetch-on-demand, except while a dispatch is in flight (above);
  // polling stops once the run is terminal.
  const runStatus = run?.status ?? '';
  React.useEffect(() => {
    if (isCourierRun && !isSettledRunStatus(runStatus)) {
      startPolling(10_000);
      return () => stopPolling();
    }
    if (awaitingOffer && !isSettledRunStatus(runStatus)) {
      startPolling(DISPATCH_POLL_INTERVAL_MS);
      return () => stopPolling();
    }
    stopPolling();
    return undefined;
  }, [isCourierRun, awaitingOffer, runStatus, startPolling, stopPolling]);
  const {
    delivery: courierDelivery,
    loading: courierLoading,
    refresh: refreshCourier,
  } = useThirdPartyDelivery(run?.id ?? null, isCourierRun);

  const [reassignDialogOpen, setReassignDialogOpen] = React.useState(false);
  const [unassignDialogOpen, setUnassignDialogOpen] = React.useState(false);

  const handleRefetch = async () => {
    await refetch();
  };

  // After a (re-)dispatch: refetch now, then keep polling until the offer lands (or the
  // bounded wait gives up — restarted here so a re-dispatch after a timeout waits again).
  const handleDispatched = async () => {
    setDispatchInFlight(true);
    setAwaitGeneration(generation => generation + 1);
    await refetch();
  };

  if (error) {
    return (
      <>
        <Toolbar
          fullscreen
          zIndex={0}
          position='relative'
          title='Run Details'
          refetch={handleRefetch}
        >
          <Button
            variant='outlined'
            size='small'
            startIcon={<MdArrowBack />}
            onClick={() => navigate('/parts-delivery/live')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Back to Runs
          </Button>
        </Toolbar>
        <DefaultFallback fullscreen severity='error' message='Failed to load run details.' />
      </>
    );
  }

  if (loading && !run) {
    return (
      <>
        <Toolbar
          fullscreen
          zIndex={0}
          position='relative'
          title='Run Details'
          refetch={handleRefetch}
        >
          <Button
            variant='outlined'
            size='small'
            startIcon={<MdArrowBack />}
            onClick={() => navigate('/parts-delivery/live')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Back to Runs
          </Button>
        </Toolbar>
        <DefaultFallback fullscreen severity='info' message='Loading run details...' />
      </>
    );
  }

  if (!run) {
    return (
      <>
        <Toolbar
          fullscreen
          zIndex={0}
          position='relative'
          title='Run Details'
          refetch={handleRefetch}
        >
          <Button
            variant='outlined'
            size='small'
            startIcon={<MdArrowBack />}
            onClick={() => navigate('/parts-delivery/live')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Back to Runs
          </Button>
        </Toolbar>
        <DefaultFallback fullscreen severity='warning' message='Run not found.' />
      </>
    );
  }

  // Who the run is with, for the dialogs: an accepted offer or the run's direct driver,
  // else the offered driver, else the targeted dispatch still landing — the same set
  // hasActiveDriver counts, so a run whose action reads Unassign always names someone.
  const displayDriver = acceptedOffer?.driver ?? run.driver ?? offeredDriver ?? pendingDriver ?? null;
  const currentDriverId = displayDriver?.id ?? null;
  const driverName = displayDriver?.user?.display_name || 'Unassigned';
  // Same semantics as MoveDetailsOffer's hasDriver: a committed driver, an in-flight
  // offer, or a targeted dispatch still landing all mean the action is "Unassign"
  // (which rescinds the offer).
  const hasActiveDriver = !!(run.driver || activeOffers.length || pendingDriver);
  // Manageable while an offer is out/claimed, and also for a parked (draft) run
  // that was previously dispatched (has a scenario) — e.g. after an unassign, so
  // the coordinator can assign it back out. The server accepts claimed /
  // dispatched / draft; a draft with no scenario was never dispatched and is
  // handled by the planner instead.
  const canManageDriver =
    canManage &&
    (run.status === 'claimed' ||
      run.status === 'dispatched' ||
      (run.status === 'draft' && !!run.scenario?.id));
  const vehicleLabel = run.vehicle
    ? `${run.vehicle.year} ${run.vehicle.make} ${run.vehicle.model}`
    : 'Unassigned';

  return (
    <>
      <Toolbar
        fullscreen
        zIndex={0}
        position='relative'
        title={`Parts Delivery Run #${run.id}`}
        refetch={handleRefetch}
      >
        <Button
          variant='outlined'
          size='small'
          startIcon={<MdArrowBack />}
          onClick={() => navigate('/parts-delivery/live')}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Back to Runs
        </Button>
      </Toolbar>

      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Run Overview Card — courier variant for external_courier runs */}
        {isCourierRun ? (
          <CourierRunOverview run={run} delivery={courierDelivery} loading={courierLoading} onRefresh={refreshCourier} />
        ) : (
        <Card>
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant='h6' sx={{ fontWeight: 700 }}>
                Run Overview
              </Typography>
              <RunStatusChip status={run.status} sx={{ fontSize: 12, fontWeight: 600 }} />
            </Box>

            {/* Driver assignment panel — mirrors MoveDetailsOffer. The single
                action button matches the moves pattern: "Unassign" (confirm
                dialog) when a driver/offer is active, "Assign Driver" (pick
                a driver) otherwise. */}
            <RunDetailOffer
              offers={activeOffers}
              runDriver={run.driver}
              pendingDriver={pendingDriver}
              canAssign={canManageDriver}
              onAssignOrUnassign={() =>
                hasActiveDriver ? setUnassignDialogOpen(true) : setReassignDialogOpen(true)
              }
              // One-step swap: the dialog in reassign mode (currentDriverId set) — the old
              // kebab's "Reassign driver…" — so the run never parks through draft.
              onReassign={() => setReassignDialogOpen(true)}
              // Stuck run: dispatched/claimed with no driver or offer showing (not draft,
              // which re-dispatches through Assign Driver).
              onUnassignWithoutDriver={
                (run.status === 'dispatched' || run.status === 'claimed') && !hasActiveDriver
                  ? () => setUnassignDialogOpen(true)
                  : undefined
              }
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>

              {/* Vehicle */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <MdDirectionsCar size={16} color='#666' />
                  <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Vehicle
                  </Typography>
                </Box>
                <Typography variant='body1' sx={{ fontWeight: 600 }}>
                  {vehicleLabel}
                </Typography>
                {run.vehicle?.license_plate && (
                  <Typography variant='caption' color='text.secondary'>
                    {run.vehicle.license_plate}
                  </Typography>
                )}
              </Box>

              {/* Departure */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <MdPlace size={16} color='#666' />
                  <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Departure Time
                  </Typography>
                </Box>
                <Typography variant='body1' sx={{ fontWeight: 600 }}>
                  {run.departure_time ? dayjs(run.departure_time).format('M/D/YYYY h:mm A') : 'Not set'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Card>
        )}

        {/* Stops & Orders */}
        {(run.stops || []).map((stop: any) => (
          <Card key={stop.id}>
            <Box sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: 14, fontWeight: 700 }}>
                  {stop.stop_order}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant='body1' sx={{ fontWeight: 700 }}>
                    {/* Recipient business (stop detail, else the orders), falling back to Stop N */}
                    {stop.delivery_stop_detail?.[0]?.recipient_business ||
                      stop.delivery_stop_detail?.[0]?.recipient_name ||
                      stop.delivery_orders?.[0]?.recipient_business ||
                      `Stop ${stop.stop_order}`}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {stop.address || 'No address'}
                  </Typography>
                </Box>
                <Chip
                  label={(stop.status || 'pending').replace(/_/g, ' ')}
                  size='small'
                  color={stop.status === 'completed' ? 'success' : stop.status === 'in_transit_to' || stop.status === 'arrived' ? 'warning' : 'default'}
                  sx={{ textTransform: 'capitalize' }}
                />
              </Box>

              {/* Geofence arrival / departure — the driver's on-site location proof
                  (device entered/left the fence around this stop's address). */}
              {(stop.geofence_arrived_at || stop.geofence_departed_at) ? (
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2, color: 'text.secondary' }}>
                  {stop.geofence_arrived_at ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <MdLogin size={15} />
                      <Typography variant='caption' sx={{ fontWeight: 600 }}>
                        Arrived {dayjs(stop.geofence_arrived_at).format('M/D h:mm A')}
                      </Typography>
                    </Box>
                  ) : null}
                  {stop.geofence_departed_at ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <MdLogout size={15} />
                      <Typography variant='caption' sx={{ fontWeight: 600 }}>
                        Departed {dayjs(stop.geofence_departed_at).format('M/D h:mm A')}
                      </Typography>
                    </Box>
                  ) : null}
                  {stop.geofence_arrived_at && stop.geofence_departed_at ? (
                    <Typography variant='caption'>
                      · {Math.max(1, dayjs(stop.geofence_departed_at).diff(stop.geofence_arrived_at, 'minute'))} min on site
                    </Typography>
                  ) : null}
                </Box>
              ) : null}

              <Divider sx={{ mb: 2 }} />

              {(stop.delivery_orders || []).length === 0 ? (
                <Box sx={{ p: 1.5, bgcolor: 'info.50', borderRadius: 1, mb: 1 }}>
                  <Typography variant='caption' sx={{ fontWeight: 700, color: 'info.main', display: 'block' }}>
                    ↩ Check-in stop — no delivery
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {stop.notes || 'Driver checks for a core / return to bring back.'}
                  </Typography>
                </Box>
              ) : (
                <>
              {/* Orders Table */}
              <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 1.5 }}>
                Delivery Orders
              </Typography>

              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Order #</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Part Number</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                    <TableCell align='center' sx={{ fontWeight: 700 }}>Qty</TableCell>
                    <TableCell align='center' sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell align='center' sx={{ fontWeight: 700 }}>Delivery</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(stop.delivery_orders || []).map((order: any) => {
                    const statusCfg = ORDER_STATUS_CHIPS[order.status] ?? { label: order.status, color: 'default' as const };
                    return (
                      <TableRow key={order.id}>
                        <TableCell sx={{ fontWeight: 600 }}>{order.order_number}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{order.part_number || '—'}</TableCell>
                        <TableCell>{order.item_description || '—'}</TableCell>
                        <TableCell align='center'>{order.quantity ?? 1}</TableCell>
                        <TableCell align='center'>
                          <Chip
                            label={statusCfg.label}
                            size='small'
                            color={statusCfg.color}
                            variant='outlined'
                            sx={{ fontSize: 11, height: 22 }}
                          />
                        </TableCell>
                        <TableCell align='center'>
                          {order.delivery_status === 'delivered' ? (
                            <Chip
                              icon={<MdCheck size={14} />}
                              label='Delivered'
                              size='small'
                              color='success'
                              sx={{ fontSize: 11, height: 22 }}
                            />
                          ) : order.delivery_status === 'partial' ? (
                            <Chip
                              icon={<MdWarning size={14} />}
                              label='Partial'
                              size='small'
                              color='warning'
                              sx={{ fontSize: 11, height: 22 }}
                            />
                          ) : order.delivery_status === 'rejected' ? (
                            <Chip
                              icon={<MdWarning size={14} />}
                              label='Rejected'
                              size='small'
                              color='error'
                              sx={{ fontSize: 11, height: 22 }}
                            />
                          ) : (
                            <Typography variant='caption' color='text.disabled'>—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
                </>
              )}

              {/* POD Info */}
              {(stop.delivery_orders || []).some(hasPod) && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant='caption' sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                    Proof of Delivery
                  </Typography>
                  {buildPodBlocks(stop.delivery_orders || []).map(({ order, note, showSignature, photos }) => {
                    const o = order as any;
                    const meta = NOTE_META[o.delivery_status] ?? { label: 'Note', color: 'text.secondary' };
                    return (
                      <Box key={o.id} sx={{ mb: 1.5, '&:last-of-type': { mb: 0 } }}>
                        <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                          {o.order_number}
                          {o.pod_signer_name ? ` — signed by ${o.pod_signer_name}` : ''}
                          {o.delivered_at ? ` at ${dayjs(o.delivered_at).format('h:mm A')}` : ''}
                        </Typography>
                        {note ? (
                          <Typography variant='caption' sx={{ display: 'block', pl: 1, color: meta.color }}>
                            {meta.label}: {note}
                          </Typography>
                        ) : null}

                        {showSignature ? (
                          <Box sx={{ pl: 1, mt: 0.5 }}>
                            <Typography variant='caption' sx={{ display: 'block', fontWeight: 600, color: 'text.secondary' }}>
                              Signature
                            </Typography>
                            <Box component='a' href={o.pod_signature_url} target='_blank' rel='noopener' sx={{ display: 'inline-block' }}>
                              <Box
                                component='img'
                                src={o.pod_signature_url}
                                alt='Signature'
                                loading='lazy'
                                sx={{
                                  height: 140,
                                  maxWidth: 360,
                                  objectFit: 'contain',
                                  bgcolor: '#fff',
                                  border: 1,
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  mt: 0.5,
                                }}
                              />
                            </Box>
                          </Box>
                        ) : null}

                        {photos.length > 0 ? (
                          <Box sx={{ pl: 1, mt: 0.5 }}>
                            <Typography variant='caption' sx={{ display: 'block', fontWeight: 600, color: 'text.secondary' }}>
                              Photos ({photos.length})
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                              {photos.map((url: string, i: number) => (
                                <Box key={i} component='a' href={url} target='_blank' rel='noopener'>
                                  <Box
                                    component='img'
                                    src={url}
                                    alt={`POD photo ${i + 1}`}
                                    loading='lazy'
                                    sx={{
                                      width: 140,
                                      height: 140,
                                      objectFit: 'cover',
                                      borderRadius: 1,
                                      border: 1,
                                      borderColor: 'divider',
                                      transition: 'opacity 0.15s',
                                      '&:hover': { opacity: 0.85 },
                                    }}
                                  />
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        ) : null}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Card>
        ))}
      </Box>

      <ReassignDriverDialog
        open={reassignDialogOpen}
        onClose={() => setReassignDialogOpen(false)}
        runId={run.id}
        currentDriverId={currentDriverId}
        currentDriverName={currentDriverId ? driverName : null}
        onDone={handleDispatched}
      />
      <UnassignDriverDialog
        open={unassignDialogOpen}
        onClose={() => setUnassignDialogOpen(false)}
        runId={run.id}
        currentDriverName={currentDriverId ? driverName : null}
        onDone={handleRefetch}
      />
    </>
  );
};

export default RunDetailPage;
